import css from "./CodeEditorPanel.sass?inline"
import defaultCode from "../devices/audio-effects/werkstatt-default.js?raw"
import {isDefined, Lifecycle, Nullable} from "@opendaw/lib-std"
import {Await, createElement} from "@opendaw/lib-jsx"
import {Clipboard, Events, Html, Keyboard, Shortcut} from "@opendaw/lib-dom"
import {MonacoFactory} from "@/monaco/factory"
import {Colors, IconSymbol} from "@opendaw/studio-enums"
import {MenuItem} from "@opendaw/studio-core"
import {StudioService} from "@/service/StudioService"
import {ThreeDots} from "@/ui/spinner/ThreeDots"
import {Button} from "@/ui/components/Button"
import {Icon} from "@/ui/components/Icon"
import {MenuButton} from "@/ui/components/MenuButton"
import {Dialogs} from "@/ui/components/dialogs"
import {EditorLoadFailure} from "@/ui/components/EditorLoadFailure"
import {Surface} from "@/ui/surface/Surface"
import {dynamicImportWithRetry} from "@/ui/components/dynamicImportWithRetry"
import {CodeEditorHandler} from "./CodeEditorHandler"
import {CodeEditorExample} from "./CodeEditorState"

const className = Html.adoptStyleSheet(css, "CodeEditorPanel")

const loadMonacoSetup = dynamicImportWithRetry(() => import("./monaco-setup"))

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

export const CodeEditorPanel = ({lifecycle, service}: Construct) => {
    let statusText = "Idle"
    let statusType: "idle" | "success" | "error" = "idle"
    const statusLabel: HTMLDivElement = (
        <div className="status idle"
             onclick={() => {
                 if (statusType !== "error") {return}
                 Clipboard.writeText(statusText)
                     .then(() => Surface.get(statusLabel)
                         .toast("Error message copied to clipboard", IconSymbol.Copy))
             }}>Idle</div>
    )
    const state = service.activeCodeEditor.unwrapOrNull()
    const handler: Nullable<CodeEditorHandler> = isDefined(state) ? state.handler : null
    const initialCode = isDefined(state) ? state.initialCode : defaultCode
    const examples: ReadonlyArray<CodeEditorExample> = isDefined(state) ? state.examples : []
    const starterPrompt = isDefined(state) ? state.starterPrompt : ""
    const setStatus = (text: string, type: "idle" | "success" | "error") => {
        statusText = text
        statusType = type
        statusLabel.textContent = text
        statusLabel.className = `status ${type}`
    }
    const nameSpan: HTMLSpanElement = (<span className="name">Code Editor</span>)
    if (isDefined(handler)) {
        lifecycle.own(handler.name.catchupAndSubscribe(owner => nameSpan.textContent = owner.getValue()))
    }
    return (
        <div className={className}>
            <Await
                factory={() => Promise.all([loadMonacoSetup().then(({monaco}) => monaco)])}
                failure={(props) => EditorLoadFailure(props)}
                loading={() => ThreeDots({style: {color: Colors.orange.toString()}})}
                success={([monaco]) => {
                    const {editor, model, container} = MonacoFactory.create({
                        monaco, lifecycle, language: "javascript",
                        uri: "file:///werkstatt.js", initialCode
                    })
                    const compileCode = async () => {
                        if (!isDefined(handler)) {
                            setStatus("No handler connected", "error")
                            return
                        }
                        try {
                            await handler.compile(editor.getValue())
                            setStatus("Successfully compiled", "success")
                        } catch (reason: unknown) {
                            setStatus(String(reason), "error")
                        }
                    }
                    if (isDefined(handler)) {
                        lifecycle.own(handler.subscribeErrors(message => setStatus(message, "error")))
                        lifecycle.own(handler.subscribeCode(code => {
                            if (editor.getValue() !== code) {editor.setValue(code)}
                        }))
                    }
                    editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.Enter, () => compileCode().finally())
                    lifecycle.own(Events.subscribe(container, "keydown", event => {
                        if (Keyboard.isControlKey(event) && event.code === "KeyS") {
                            compileCode()
                                .then(() => service.projectProfileService.save().finally())
                                .finally()
                            event.preventDefault()
                            event.stopPropagation()
                        }
                    }, {capture: true}))
                    const close = () => service.closeCodeEditor()
                    return (
                        <div className="content">
                            <header>
                                <Button lifecycle={lifecycle}
                                        onClick={close}
                                        appearance={{
                                            tooltip: "Close editor",
                                            color: Colors.red,
                                            framed: true,
                                            cursor: "pointer"
                                        }}>
                                    <Icon symbol={IconSymbol.Close}/>
                                </Button>
                                {nameSpan}
                                <Button lifecycle={lifecycle}
                                        onClick={compileCode}
                                        appearance={{
                                            tooltip: `Run (${Shortcut.of("Enter", {alt: true}).format()})`,
                                            color: Colors.green,
                                            cursor: "pointer"
                                        }}>
                                    <span>Run</span> <Icon symbol={IconSymbol.Play}/>
                                </Button>
                                <Button lifecycle={lifecycle}
                                        onClick={async () => {
                                            const approved = await Dialogs.approve({
                                                headline: "Run Clipboard",
                                                message: "This will replace all code in the editor with the clipboard content and run it.",
                                                approveText: "Replace & Run",
                                                reverse: true
                                            })
                                            if (!approved) {return}
                                            const text = await Clipboard.readText()
                                            editor.executeEdits("clipboard", [{
                                                range: model.getFullModelRange(),
                                                text
                                            }])
                                            await compileCode()
                                        }}
                                        appearance={{tooltip: "Paste from clipboard and run", cursor: "pointer"}}>
                                    <span>Run Clipboard</span> <Icon symbol={IconSymbol.Paste}/>
                                </Button>
                                {starterPrompt.length > 0 && (
                                    <Button lifecycle={lifecycle}
                                            onClick={() => Clipboard.writeText(starterPrompt)
                                                .then(() => Surface.get(statusLabel).toast("AI prompt copied to clipboard", IconSymbol.Copy))
                                                .catch(reason => setStatus(String(reason), "error"))}
                                            appearance={{
                                                tooltip: "Copy AI starter prompt to clipboard",
                                                cursor: "pointer"
                                            }}>
                                        <span>Get Prompt</span> <Icon symbol={IconSymbol.Copy}/>
                                    </Button>
                                )}
                                {examples.length > 0 && (
                                    <MenuButton root={MenuItem.root()
                                        .setRuntimeChildrenProcedure(parent => parent
                                            .addMenuItem(...examples
                                                .map(example => MenuItem.default({label: example.name})
                                                    .setTriggerProcedure(() => {
                                                        editor.setValue(example.code)
                                                        compileCode().finally()
                                                    }))))}
                                                appearance={{tinyTriangle: true, color: Colors.dark}}>
                                        <span>Examples</span>
                                    </MenuButton>
                                )}
                            </header>
                            {container}
                            {statusLabel}
                        </div>
                    )
                }}/>
        </div>
    )
}
